/**
 * LCFPIAssessmentWorkflow.js
 * Assessment Progress Tracker + 4 Guided Assessment Wizards
 *
 * Sections:
 *  - AssessmentProgressTracker: 8-step progress display
 *  - ClimateWizard, CircularityWizard, FinanceWizard, CarbonWizard
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import {
  CheckCircle2, Clock, ChevronRight, Loader, BarChart2,
  Leaf, DollarSign, Zap, X, Upload, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const STEP_ICONS = {
  registration:    CheckCircle2,
  climate:         BarChart2,
  circularity:     Leaf,
  finance:         DollarSign,
  carbon:          Zap,
  evidence:        Upload,
  review:          CheckCircle2,
  investment_ready:CheckCircle2,
};

const STEP_COLORS = {
  complete:    { bg:"#14532d20", border:"#22c55e", text:"#22c55e" },
  in_progress: { bg:"#1e3a5f20", border:"#60a5fa", text:"#60a5fa" },
  pending:     { bg:"#1e293b",   border:"#334155", text:"#475569" },
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PROGRESS TRACKER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function AssessmentProgressTracker({ projectId, onStartStep }) {
  const [workflow, setWorkflow] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const load = () => {
    if (!projectId) return;
    client.get(`/lcfpi/projects/${projectId}/assessment-workflow`)
      .then(r => setWorkflow(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [projectId]);

  if (loading) return (
    <div className="flex justify-center py-8"><Loader size={16} className="animate-spin text-slate-500"/></div>
  );
  if (!workflow) return (
    <p className="text-xs text-slate-500 text-center py-4">Workflow data unavailable</p>
  );

  const { steps = [], pct_complete = 0, next_step } = workflow;

  return (
    <div data-testid="assessment-progress-tracker">
      {/* Progress bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Assessment Progress
          </p>
          <p className="text-[10px] font-bold text-blue-400">{pct_complete}% complete</p>
        </div>
        <div className="h-2 rounded-full bg-slate-800">
          <div className="h-2 rounded-full transition-all duration-500"
            style={{ width:`${pct_complete}%`, background:"linear-gradient(90deg,#3b82f6,#22c55e)" }}/>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, i) => {
          const Icon  = STEP_ICONS[step.step_id] || CheckCircle2;
          const style = STEP_COLORS[step.status] || STEP_COLORS.pending;
          const isNext = next_step?.step_id === step.step_id;
          const canStart = ["climate","circularity","finance","carbon"].includes(step.step_id);

          return (
            <div key={step.step_id}
              data-testid={`workflow-step-${step.step_id}`}
              className="flex items-center gap-3 rounded-xl px-4 py-3 transition-all"
              style={{ background:style.bg, border:`1px solid ${style.border}` }}>

              {/* Step number & icon */}
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className="text-[9px] font-black" style={{ color:style.text, width:14 }}>{i+1}</span>
                {step.status === "complete"
                  ? <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                  : <Icon size={14} style={{ color:style.text }} className="flex-shrink-0" />
                }
              </div>

              {/* Step label */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold" style={{ color: step.status==="complete"?"#22c55e":"#e2e8f0" }}>
                  {step.step_label}
                </p>
                {step.status === "complete" && step.completed_at && (
                  <p className="text-[9px] text-slate-500">
                    Completed {new Date(step.completed_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}
                    {step.score > 0 && <span className="text-green-400 ml-2">Score: {step.score.toFixed(0)}/100</span>}
                  </p>
                )}
                {step.status === "pending" && isNext && (
                  <p className="text-[9px] text-blue-400">Next recommended action</p>
                )}
              </div>

              {/* Status badge + action */}
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background:`${style.border}20`, color:style.text, border:`1px solid ${style.border}30` }}>
                  {step.status === "complete" ? "Complete" :
                   step.status === "in_progress" ? "In Progress" : "Pending"}
                </span>
                {canStart && step.status !== "complete" && onStartStep && (
                  <button
                    data-testid={`start-step-${step.step_id}`}
                    onClick={() => onStartStep(step.step_id)}
                    className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg"
                    style={{ background:"#1d4ed8", color:"#fff" }}>
                    Start <ChevronRight size={10}/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WIZARD SHELL
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function WizardShell({ title, subtitle, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background:"#000000bb" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="flex flex-col rounded-2xl border border-slate-700"
        style={{ width:"min(600px,96vw)", maxHeight:"85vh", background:"#060d1a" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div>
            <p className="text-sm font-black text-white">{title}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose}><X size={16} className="text-slate-400 hover:text-slate-300"/></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

const INPUT_CLS = "w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-blue-500 placeholder-slate-600";
const LABEL_CLS = "text-xs text-slate-400 mb-1 block font-medium";
function Field({ label, children, required }) {
  return <div><label className={LABEL_CLS}>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>{children}</div>;
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CLIMATE ASSESSMENT WIZARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function ClimateAssessmentWizard({ projectId, onClose, onComplete }) {
  const [form, setForm] = useState({
    electricity_kwh: "", diesel_litres: "", petrol_litres: "",
    natural_gas_m3: "", renewable_kwh: "", process_emissions_tco2e: "",
    energy_efficiency_plan: false, renewable_energy_installed: false,
    climate_policy: false,
  });
  const [running, setRunning] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const run = async () => {
    setRunning(true);
    try {
      // Update project with energy data, then trigger assessment
      await client.put(`/lcfpi/projects/${projectId}`, {
        electricity_kwh: Number(form.electricity_kwh) || 0,
        diesel_litres:   Number(form.diesel_litres)   || 0,
        petrol_litres:   Number(form.petrol_litres)   || 0,
        natural_gas_m3:  Number(form.natural_gas_m3)  || 0,
        renewable_kwh:   Number(form.renewable_kwh)   || 0,
        process_emissions_tco2e: Number(form.process_emissions_tco2e) || 0,
        energy_efficiency_plan:    form.energy_efficiency_plan    ? 1 : 0,
        renewable_energy_installed: form.renewable_energy_installed ? 1 : 0,
        climate_policy:            form.climate_policy            ? 1 : 0,
      });
      const r = await client.post(`/lcfpi/assess/${projectId}`, {});
      const score = r.data?.calc?.climate_score || 0;
      // Mark workflow step complete
      await client.post(`/lcfpi/projects/${projectId}/assessment-workflow/climate/complete`, {
        score, notes: "Climate assessment completed via guided wizard", data: form,
      });
      toast.success(`Climate Assessment complete — Score: ${score.toFixed(0)}/100`);
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Climate assessment failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <WizardShell title="Climate Assessment" subtitle="Energy consumption, emissions baseline &amp; reduction potential" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl px-4 py-3 mb-4">
          <p className="text-xs text-blue-300 leading-relaxed">
            Provide your current energy consumption data. The platform will automatically calculate
            your climate score, baseline emissions, and emission reduction potential.
          </p>
        </div>

        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Energy Consumption (Annual)</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Electricity (kWh/yr)">
            <input type="number" value={form.electricity_kwh} onChange={e=>set("electricity_kwh",e.target.value)}
              placeholder="500000" className={INPUT_CLS}/>
          </Field>
          <Field label="Diesel (Litres/yr)">
            <input type="number" value={form.diesel_litres} onChange={e=>set("diesel_litres",e.target.value)}
              placeholder="60000" className={INPUT_CLS}/>
          </Field>
          <Field label="Petrol (Litres/yr)">
            <input type="number" value={form.petrol_litres} onChange={e=>set("petrol_litres",e.target.value)}
              placeholder="0" className={INPUT_CLS}/>
          </Field>
          <Field label="Natural Gas (m³/yr)">
            <input type="number" value={form.natural_gas_m3} onChange={e=>set("natural_gas_m3",e.target.value)}
              placeholder="0" className={INPUT_CLS}/>
          </Field>
          <Field label="Renewable Electricity (kWh/yr)">
            <input type="number" value={form.renewable_kwh} onChange={e=>set("renewable_kwh",e.target.value)}
              placeholder="0" className={INPUT_CLS}/>
          </Field>
          <Field label="Process Emissions (tCO₂e/yr)">
            <input type="number" value={form.process_emissions_tco2e} onChange={e=>set("process_emissions_tco2e",e.target.value)}
              placeholder="0" className={INPUT_CLS}/>
          </Field>
        </div>

        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2">Climate Policies</p>
        <div className="space-y-2">
          {[
            ["energy_efficiency_plan",     "Energy Efficiency Plan in place"],
            ["renewable_energy_installed", "Renewable Energy Installed"],
            ["climate_policy",             "Climate Policy / Decarbonisation Strategy"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/30 cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-blue-500 w-4 h-4"/>
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>

        <button onClick={run} disabled={running} data-testid="climate-wizard-run"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm disabled:opacity-50"
          style={{ background:"linear-gradient(135deg,#1d4ed8,#059669)", color:"#fff" }}>
          {running ? <Loader size={14} className="animate-spin"/> : <BarChart2 size={14}/>}
          {running ? "Running Assessment…" : "Run Climate Assessment"}
        </button>
      </div>
    </WizardShell>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CIRCULARITY ASSESSMENT WIZARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function CircularityAssessmentWizard({ projectId, onClose, onComplete }) {
  const [form, setForm] = useState({
    waste_generated_t: "", waste_diverted_pct: "", recycled_input_pct: "",
    materials_recovered_t: "", circular_model_maturity: 1,
    industrial_symbiosis: false, iso_certification: false,
    quality_management_system: false,
  });
  const [running, setRunning] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const run = async () => {
    setRunning(true);
    try {
      await client.put(`/lcfpi/projects/${projectId}`, {
        waste_generated_t:     Number(form.waste_generated_t)     || 0,
        waste_diverted_pct:    Number(form.waste_diverted_pct)    || 0,
        recycled_input_pct:    Number(form.recycled_input_pct)    || 0,
        materials_recovered_t: Number(form.materials_recovered_t) || 0,
        circular_model_maturity: Number(form.circular_model_maturity) || 1,
        industrial_symbiosis:   form.industrial_symbiosis    ? 1 : 0,
        iso_certification:      form.iso_certification       ? 1 : 0,
        quality_management_system: form.quality_management_system ? 1 : 0,
      });
      const r = await client.post(`/lcfpi/assess/${projectId}`, {});
      const score = r.data?.calc?.circularity_score || 0;
      await client.post(`/lcfpi/projects/${projectId}/assessment-workflow/circularity/complete`, {
        score, notes: "Circularity assessment via guided wizard", data: form,
      });
      toast.success(`Circularity Assessment complete — Score: ${score.toFixed(0)}/100`);
      onComplete?.(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Circularity assessment failed");
    } finally { setRunning(false); }
  };

  return (
    <WizardShell title="Circularity Assessment" subtitle="Waste streams, material recovery &amp; resource productivity" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-green-900/20 border border-green-700/30 rounded-xl px-4 py-3 mb-4">
          <p className="text-xs text-green-300 leading-relaxed">
            Provide information on your material flows, waste generation and recovery activities.
            The platform will calculate your circularity score and identify opportunities.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Waste Generated (t/yr)">
            <input type="number" value={form.waste_generated_t} onChange={e=>set("waste_generated_t",e.target.value)} placeholder="1000" className={INPUT_CLS}/>
          </Field>
          <Field label="Waste Diverted (%)">
            <input type="number" value={form.waste_diverted_pct} onChange={e=>set("waste_diverted_pct",e.target.value)} min={0} max={100} placeholder="30" className={INPUT_CLS}/>
          </Field>
          <Field label="Materials Recovered (t/yr)">
            <input type="number" value={form.materials_recovered_t} onChange={e=>set("materials_recovered_t",e.target.value)} placeholder="300" className={INPUT_CLS}/>
          </Field>
          <Field label="Recycled Input (%)">
            <input type="number" value={form.recycled_input_pct} onChange={e=>set("recycled_input_pct",e.target.value)} min={0} max={100} placeholder="20" className={INPUT_CLS}/>
          </Field>
          <Field label="Circular Model Maturity (1-5)">
            <select value={form.circular_model_maturity} onChange={e=>set("circular_model_maturity",Number(e.target.value))} className={INPUT_CLS}>
              <option value={1}>1 — Early stage</option>
              <option value={2}>2 — Developing</option>
              <option value={3}>3 — Established</option>
              <option value={4}>4 — Advanced</option>
              <option value={5}>5 — Leading</option>
            </select>
          </Field>
        </div>
        <div className="space-y-2">
          {[
            ["industrial_symbiosis","Industrial Symbiosis / Waste Exchange with other businesses"],
            ["iso_certification","ISO Certification (ISO 9001, 14001 or equivalent)"],
            ["quality_management_system","Quality Management System implemented"],
          ].map(([k,label]) => (
            <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/30 cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-green-500 w-4 h-4"/>
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>
        <button onClick={run} disabled={running} data-testid="circularity-wizard-run"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm disabled:opacity-50"
          style={{ background:"linear-gradient(135deg,#059669,#0d9488)", color:"#fff" }}>
          {running ? <Loader size={14} className="animate-spin"/> : <Leaf size={14}/>}
          {running ? "Running Assessment…" : "Run Circularity Assessment"}
        </button>
      </div>
    </WizardShell>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CLIMATE FINANCE ASSESSMENT WIZARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function FinanceAssessmentWizard({ projectId, onClose, onComplete }) {
  const [form, setForm] = useState({
    collateral_available: false, audited_accounts: false, bank_statements_3yr: false,
    board_structure: false, esg_policy: false, sustainability_report: false,
    gender_policy: false, anti_corruption_policy: false,
    scalability_score: 3, offtake_agreements: false, market_study: false,
    annual_revenue_range: "", ebitda_range: "",
  });
  const [running, setRunning] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const run = async () => {
    setRunning(true);
    try {
      await client.put(`/lcfpi/projects/${projectId}`, {
        collateral_available:  form.collateral_available    ? 1 : 0,
        audited_accounts:      form.audited_accounts        ? 1 : 0,
        bank_statements_3yr:   form.bank_statements_3yr     ? 1 : 0,
        board_structure:       form.board_structure         ? 1 : 0,
        esg_policy:            form.esg_policy              ? 1 : 0,
        sustainability_report: form.sustainability_report   ? 1 : 0,
        gender_policy:         form.gender_policy           ? 1 : 0,
        anti_corruption_policy:form.anti_corruption_policy  ? 1 : 0,
        scalability_score:     Number(form.scalability_score) || 3,
        offtake_agreements:    form.offtake_agreements      ? 1 : 0,
        market_study:          form.market_study            ? 1 : 0,
        annual_revenue_range:  form.annual_revenue_range,
        ebitda_range:          form.ebitda_range,
      });
      const r = await client.post(`/lcfpi/assess/${projectId}`, {});
      const score = r.data?.calc?.finance_readiness?.score || r.data?.calc?.overall_score || 0;
      await client.post(`/lcfpi/projects/${projectId}/assessment-workflow/finance/complete`, {
        score, notes: "Finance readiness assessed via guided wizard", data: form,
      });
      toast.success(`Climate Finance Assessment complete — Score: ${score.toFixed(0)}/100`);
      onComplete?.(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Finance assessment failed");
    } finally { setRunning(false); }
  };

  return (
    <WizardShell title="Climate Finance Assessment" subtitle="Finance readiness, governance &amp; DFI eligibility" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl px-4 py-3 mb-2">
          <p className="text-xs text-amber-300 leading-relaxed">
            Assess financial documentation, governance maturity and investor readiness. These factors
            determine DFI eligibility and investment readiness scores.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <Field label="Annual Revenue Range">
            <select value={form.annual_revenue_range} onChange={e=>set("annual_revenue_range",e.target.value)} className={INPUT_CLS}>
              {["","<$1M","$1M-$5M","$5M-$20M","$20M-$100M",">$100M"].map(r=><option key={r} value={r}>{r||"Select range"}</option>)}
            </select>
          </Field>
          <Field label="EBITDA Range">
            <select value={form.ebitda_range} onChange={e=>set("ebitda_range",e.target.value)} className={INPUT_CLS}>
              {["","Negative","$0-$500K","$500K-$2M","$2M-$10M",">$10M"].map(r=><option key={r} value={r}>{r||"Select range"}</option>)}
            </select>
          </Field>
        </div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Financial Documentation</p>
        <div className="space-y-2">
          {[
            ["audited_accounts",    "3-Year Audited Financial Statements"],
            ["bank_statements_3yr", "3-Year Bank Statements Available"],
            ["collateral_available","Collateral / Security Available"],
            ["market_study",        "Market Study / Demand Assessment Completed"],
            ["offtake_agreements",  "Offtake Agreements in Place"],
          ].map(([k,label]) => (
            <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/30 cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-amber-500 w-4 h-4"/>
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Governance & ESG</p>
        <div className="space-y-2">
          {[
            ["board_structure",       "Formal Board / Governance Structure"],
            ["esg_policy",            "ESG Policy / Sustainability Statement"],
            ["sustainability_report", "Sustainability Report Published"],
            ["gender_policy",         "Gender & Inclusion Policy"],
            ["anti_corruption_policy","Anti-Corruption Policy"],
          ].map(([k,label]) => (
            <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/30 cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-amber-500 w-4 h-4"/>
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>
        <button onClick={run} disabled={running} data-testid="finance-wizard-run"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm disabled:opacity-50"
          style={{ background:"linear-gradient(135deg,#d97706,#1d4ed8)", color:"#fff" }}>
          {running ? <Loader size={14} className="animate-spin"/> : <DollarSign size={14}/>}
          {running ? "Running Assessment…" : "Run Finance Readiness Assessment"}
        </button>
      </div>
    </WizardShell>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CARBON MONETISATION ASSESSMENT WIZARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function CarbonAssessmentWizard({ projectId, onClose, onComplete }) {
  const [form, setForm] = useState({
    carbon_credit_interest: true, has_baseline_data: false,
    monitoring_data_available: false, baseline_confirmation: "estimated",
    poa_interest: false,
  });
  const [result,   setResult]   = useState(null);
  const [running,  setRunning]  = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const run = async () => {
    setRunning(true);
    try {
      const r = await client.get(`/lcfpi/monetisation/${projectId}`);
      setResult(r.data);
      await client.post(`/lcfpi/projects/${projectId}/assessment-workflow/carbon/complete`, {
        score: 40, notes: "Carbon monetisation screening completed", data: form,
      });
      toast.success("Carbon Monetisation Assessment complete");
      onComplete?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Carbon assessment failed");
    } finally { setRunning(false); }
  };

  return (
    <WizardShell title="Carbon Monetisation Assessment" subtitle="Baseline data, methodology &amp; pathway recommendation" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl px-4 py-3 mb-2">
          <p className="text-xs text-purple-300 leading-relaxed">
            Assess your project's eligibility for carbon market participation. The platform will
            recommend the most suitable carbon standard and estimate revenue potential.
          </p>
        </div>
        <div className="space-y-2">
          {[
            ["carbon_credit_interest",   "Carbon Credit Interest — actively seeking carbon market participation"],
            ["has_baseline_data",        "Existing Baseline Data — emissions monitoring data available"],
            ["monitoring_data_available","Monitoring Data Available — meter/measurement data in place"],
            ["poa_interest",             "Programme of Activities Interest — open to portfolio aggregation"],
          ].map(([k,label]) => (
            <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/30 cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-purple-500 w-4 h-4"/>
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>
        <Field label="Baseline Confirmation Status">
          <select value={form.baseline_confirmation} onChange={e=>set("baseline_confirmation",e.target.value)} className={INPUT_CLS}>
            <option value="estimated">Estimated — no measurement yet</option>
            <option value="measured">Measured — activity data available</option>
            <option value="site_survey">Site Survey — field-confirmed</option>
          </select>
        </Field>

        {result && (
          <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4">
            <p className="text-xs font-bold text-green-400 mb-2">Carbon Screening Results</p>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-base font-black text-green-400">{result.carbon?.qualification_funnel?.monetisable || "—"} t/yr</p><p className="text-[10px] text-slate-500">Monetisable tCO₂e</p></div>
              <div><p className="text-base font-black text-amber-400">${((result.carbon?.qualification_funnel?.monetisable || 0) * 15).toLocaleString()}/yr</p><p className="text-[10px] text-slate-500">Est. Revenue at $15/VCU</p></div>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">Recommended pathway: {result.carbon?.methodology?.label || "Verra VCS"}</p>
          </div>
        )}

        <button onClick={run} disabled={running} data-testid="carbon-wizard-run"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm disabled:opacity-50"
          style={{ background:"linear-gradient(135deg,#7c3aed,#1d4ed8)", color:"#fff" }}>
          {running ? <Loader size={14} className="animate-spin"/> : <Zap size={14}/>}
          {running ? "Running Assessment…" : "Run Carbon Screening"}
        </button>
      </div>
    </WizardShell>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ASSESSMENT WIZARD DISPATCHER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function AssessmentWizardDispatcher({ projectId, stepId, onClose, onComplete }) {
  if (!stepId || !projectId) return null;

  const props = { projectId, onClose, onComplete };
  const wizards = {
    climate:     <ClimateAssessmentWizard     {...props} />,
    circularity: <CircularityAssessmentWizard {...props} />,
    finance:     <FinanceAssessmentWizard     {...props} />,
    carbon:      <CarbonAssessmentWizard      {...props} />,
  };
  return wizards[stepId] || null;
}
